import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from '../../services/api';
import StatusBadge from '../../components/shared/StatusBadge';
import ProgressBar from '../../components/shared/ProgressBar';

const SubmissionDetail = () => {
  const { id } = useParams();
  const [submission, setSubmission] = useState(null);
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeSectionIndex, setActiveSectionIndex] = useState(0);
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewStatus, setReviewStatus] = useState('');
  const [finalising, setFinalising] = useState(false);
  const [overallResult, setOverallResult] = useState('');
  const [inspectorNotes, setInspectorNotes] = useState('');

  useEffect(() => {
    fetchSubmission();
  }, [id]);

  const fetchSubmission = async () => {
    try {
      const response = await api.get(`/submissions/${id}`);
      if (response.data.success) {
        const data = response.data.data;
        setSubmission(data.submission);
        setSections(data.sections);
        
        // Set initial review notes from existing reviews
        if (data.sections[0]?.review) {
          setReviewNotes(data.sections[0].review.notes || '');
          setReviewStatus(data.sections[0].review.status || '');
        }
        
        // Set overall result if already finalised
        if (data.submission.overall_result) {
          setOverallResult(data.submission.overall_result);
        }
        if (data.submission.inspector_notes) {
          setInspectorNotes(data.submission.inspector_notes);
        }
      }
    } catch (error) {
      setError(error.response?.data?.message || 'Failed to load submission');
    } finally {
      setLoading(false);
    }
  };

  const handleSectionChange = (index) => {
    setActiveSectionIndex(index);
    const section = sections[index];
    if (section?.review) {
      setReviewNotes(section.review.notes || '');
      setReviewStatus(section.review.status || '');
    } else {
      setReviewNotes('');
      setReviewStatus('');
    }
  };

  const handleSaveReview = async () => {
    try {
      const section = sections[activeSectionIndex];
      await api.put(`/inspector/submissions/${id}/sections/${section.id}/review`, {
        status: reviewStatus,
        notes: reviewNotes
      });
      
      // Refresh submission data
      await fetchSubmission();
      alert('Review saved successfully');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to save review');
    }
  };

  const handleFinalise = async () => {
    if (!overallResult) {
      alert('Please select an overall result');
      return;
    }

    setFinalising(true);
    try {
      await api.put(`/inspector/submissions/${id}/finalise`, {
        overallResult,
        inspectorNotes
      });
      
      // Refresh submission data
      await fetchSubmission();
      alert('Submission finalised successfully');
    } catch (error) {
      alert(error.response?.data?.message || 'Failed to finalise submission');
    } finally {
      setFinalising(false);
    }
  };

  const downloadDocument = (docId) => {
    window.open(`${process.env.REACT_APP_API_URL || 'http://localhost:5000/api'}/documents/${docId}/download`, '_blank');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
        {error}
      </div>
    );
  }

  const currentSection = sections[activeSectionIndex];
  const allSectionsReviewed = sections.every(s => s.review?.status);

  return (
    <div>
      {/* Header */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="text-2xl font-bold text-dark">{submission.firm_name}</h1>
            <p className="text-grey mt-1">
              Submitted by: {submission.first_name} {submission.last_name}
            </p>
            <p className="text-sm text-lightGrey mt-1">
              Submitted: {submission.submitted_at ? new Date(submission.submitted_at).toLocaleString() : '-'}
            </p>
          </div>
          <div className="text-right">
            <StatusBadge status={submission.status} />
            <div className="mt-2">
              <ProgressBar percentage={submission.completion_percentage || 0} />
            </div>
          </div>
        </div>
      </div>

      {/* Section Tabs */}
      <div className="flex overflow-x-auto space-x-2 mb-6 pb-2">
        {sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => handleSectionChange(index)}
            className={`flex-shrink-0 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              index === activeSectionIndex
                ? 'bg-primary text-white'
                : section.review?.status
                ? 'bg-green-100 text-primary'
                : 'bg-white text-grey border border-border hover:border-primary'
            }`}
          >
            {index + 1}. {section.title}
            {section.review?.status && (
              <span className="ml-2 text-xs">✓</span>
            )}
          </button>
        ))}
      </div>

      {currentSection && (
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          {/* Section Header */}
          <div
            className="mb-6 pb-4 border-b border-border"
            style={{ borderLeftColor: currentSection.accent_color, borderLeftWidth: '4px' }}
          >
            <h2 className="text-xl font-bold text-dark mb-2">{currentSection.title}</h2>
            <p className="text-sm text-lightGrey">{currentSection.department}</p>
          </div>

          {/* Questions */}
          <div className="space-y-4 mb-8">
            <h3 className="text-lg font-semibold text-dark mb-4">Questions</h3>
            {currentSection.questions.map((question) => (
              <div key={question.id} className="border border-border rounded-lg p-4">
                <p className="text-sm font-medium text-grey mb-2">
                  {question.order_index}. {question.question_text}
                </p>
                <div className="bg-offWhite p-3 rounded">
                  <p className="text-sm text-dark">
                    <strong>Answer:</strong> {question.answer || 'Not answered'}
                  </p>
                </div>
              </div>
            ))}
          </div>

          {/* Documents */}
          <div className="space-y-4 mb-8">
            <h3 className="text-lg font-semibold text-dark mb-4">Uploaded Documents</h3>
            {currentSection.document_requirements.map((req) => (
              <div key={req.id} className="border border-border rounded-lg p-4">
                <p className="text-sm font-medium text-grey mb-3">
                  {req.order_index}. {req.name}
                </p>
                {req.uploaded_documents && req.uploaded_documents.length > 0 ? (
                  <div className="space-y-2">
                    {req.uploaded_documents.map((doc) => (
                      <div key={doc.id} className="flex items-center justify-between bg-offWhite p-3 rounded">
                        <div className="flex items-center space-x-3">
                          <svg className="h-5 w-5 text-grey" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span className="text-sm text-grey">{doc.original_name}</span>
                        </div>
                        <button
                          onClick={() => downloadDocument(doc.id)}
                          className="text-primary hover:text-green-700 text-sm"
                        >
                          Download
                        </button>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-lightGrey">No documents uploaded</p>
                )}
              </div>
            ))}
          </div>

          {/* Section Review */}
          <div className="border-t border-border pt-6">
            <h3 className="text-lg font-semibold text-dark mb-4">Section Review</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Review Status</label>
                <select
                  value={reviewStatus}
                  onChange={(e) => setReviewStatus(e.target.value)}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                >
                  <option value="">Select status...</option>
                  <option value="approved">Approved</option>
                  <option value="flagged">Flagged</option>
                  <option value="needs_more_info">Needs More Info</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-grey mb-2">Review Notes</label>
                <textarea
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={4}
                  className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                  placeholder="Add your review notes..."
                />
              </div>
              <button
                onClick={handleSaveReview}
                disabled={!reviewStatus}
                className="px-6 py-2 bg-primary text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Save Review
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Finalise Section */}
      {allSectionsReviewed && submission.status !== 'approved' && submission.status !== 'rejected' && submission.status !== 'requires_resubmission' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-dark mb-4">Finalise Submission</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-grey mb-2">Overall Result</label>
              <select
                value={overallResult}
                onChange={(e) => setOverallResult(e.target.value)}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
              >
                <option value="">Select result...</option>
                <option value="approved">Approved</option>
                <option value="rejected">Rejected</option>
                <option value="requires_resubmission">Requires Resubmission</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-grey mb-2">Inspector Notes</label>
              <textarea
                value={inspectorNotes}
                onChange={(e) => setInspectorNotes(e.target.value)}
                rows={4}
                className="w-full px-4 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                placeholder="Add overall inspector notes..."
              />
            </div>
            <button
              onClick={handleFinalise}
              disabled={finalising || !overallResult}
              className="px-6 py-2 bg-orange text-white rounded-lg hover:bg-orange-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {finalising ? 'Finalising...' : 'Finalise Submission'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubmissionDetail;
